import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { envApiUrl } from '../fetchHooks/envApiUrl';
import type { MydayType } from '../fetchHooks/useMyday';

// Helper function to fetch image as blob, compress it, and convert to base64
const fetchImageAsBase64 = async (url: string, maxWidth = 3840, maxHeight = 2160, quality = 0.92): Promise<string> => {
  try {
    const response = await fetch(url, {
      credentials: 'include', // Include credentials for authenticated requests
      mode: 'cors',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Create image from blob and compress it
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }

        // Create canvas and draw compressed image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Use high-quality image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with compression
        const base64 = canvas.toDataURL('image/jpeg', quality);
        resolve(base64);
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      img.src = URL.createObjectURL(blob);
    });
  } catch (error) {
    console.error('Error fetching image:', url, error);
    throw error;
  }
};

// Helper function to load images with higher resolution and convert to base64
const loadHighResImages = async (containerElement: HTMLElement): Promise<void> => {
  const images = containerElement.querySelectorAll('img');
  const imagePromises: Promise<void>[] = [];

  images.forEach((img) => {
    const promise = new Promise<void>((resolve) => {
      // Check if image is from our API
      const src = img.src;
      if (src.includes('/files/custom-thumbnail/')) {
        // Extract image ID from URL
        const match = src.match(/\/files\/custom-thumbnail\/(\d+)/);
        if (match) {
          const imageId = match[1];
          // Load high resolution version for export (3840x2160 for high quality export)
          const highResSrc = `${envApiUrl}/files/custom-thumbnail/${imageId}?maxWidth=3840&maxHeight=2160&quality=95&format=jpeg`;

          // Fetch image with credentials, compress it, and convert to base64
          fetchImageAsBase64(highResSrc, 3840, 2160, 0.92)
            .then((base64) => {
              img.src = base64;
              resolve();
            })
            .catch((error) => {
              console.error('Failed to fetch and convert image to base64:', error);
              // Fallback: try to use the high-res URL directly
              img.src = highResSrc;
              // Wait for image to load
              const fallbackImg = new Image();
              fallbackImg.onload = () => resolve();
              fallbackImg.onerror = () => resolve(); // Continue even if it fails
              fallbackImg.src = highResSrc;
            });
        } else {
          // Try to fetch existing image
          fetchImageAsBase64(img.src)
            .then((base64) => {
              img.src = base64;
              resolve();
            })
            .catch(() => resolve()); // Continue even if conversion fails
        }
      } else {
        // For non-API images, try to fetch them
        fetchImageAsBase64(img.src)
          .then((base64) => {
            img.src = base64;
            resolve();
          })
          .catch(() => {
            // If fetch fails, image might be from same origin, try direct conversion
            if (img.complete && img.naturalWidth > 0) {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0);
                try {
                  img.src = canvas.toDataURL('image/jpeg', 0.95);
                } catch (e) {
                  // Ignore errors
                }
              }
            }
            resolve();
          });
      }
    });
    imagePromises.push(promise);
  });

  await Promise.all(imagePromises);
  // Wait a bit more for images to fully render
  await new Promise((resolve) => setTimeout(resolve, 1000));
};

export const exportMydayToPDF = async (myday: MydayType, containerElement: HTMLElement) => {
  try {
    // Hide elements that shouldn't be in PDF before capturing
    const buttons = containerElement.querySelectorAll('button');
    const originalButtonStyles: Array<{ element: HTMLElement; display: string }> = [];
    buttons.forEach((btn) => {
      const htmlBtn = btn as HTMLElement;
      originalButtonStyles.push({ element: htmlBtn, display: htmlBtn.style.display });
      htmlBtn.style.display = 'none';
    });

    // Hide checkboxes
    const checkboxes = containerElement.querySelectorAll('input[type="checkbox"]');
    const originalCheckboxStyles: Array<{ element: HTMLElement; display: string }> = [];
    checkboxes.forEach((checkbox) => {
      const htmlCheckbox = checkbox as HTMLElement;
      originalCheckboxStyles.push({ element: htmlCheckbox, display: htmlCheckbox.style.display });
      htmlCheckbox.style.display = 'none';
    });

    // Hide navbar if present
    const navbar = document.querySelector('[class*="navbar"], [class*="Navbar"]') as HTMLElement;
    const originalNavbarDisplay = navbar?.style.display;
    if (navbar) {
      navbar.style.display = 'none';
    }

    // Load high-resolution images before capturing
    await loadHighResImages(containerElement);

    // Wait a bit for styles to apply
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Capture the content as canvas with high scale for better quality
    const canvas = await html2canvas(containerElement, {
      scale: 3, // Increased to 3 for higher quality export
      useCORS: false, // Not needed since we're using base64 images
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: containerElement.scrollWidth,
      windowHeight: containerElement.scrollHeight,
      allowTaint: false, // Not needed with base64 images
      removeContainer: false,
      imageTimeout: 30000, // Increase timeout for large images
      onclone: (clonedDoc) => {
        // Ensure all images in the cloned document are loaded
        const clonedImages = clonedDoc.querySelectorAll('img');
        clonedImages.forEach((img) => {
          if (img.src?.startsWith('data:')) {
            // Image is already base64, ensure it's loaded
            const testImg = new Image();
            testImg.src = img.src;
          }
        });
      },
    });

    // Restore original styles
    originalButtonStyles.forEach(({ element, display }) => {
      element.style.display = display;
    });
    originalCheckboxStyles.forEach(({ element, display }) => {
      element.style.display = display;
    });
    if (navbar) {
      navbar.style.display = originalNavbarDisplay || '';
    }

    // Convert canvas to JPEG with higher quality for better image export
    const imgData = canvas.toDataURL('image/jpeg', 0.92); // Use JPEG with 92% quality for better export
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    const pdf = new jsPDF('p', 'mm', 'a4');
    let position = 0;

    // Add first page
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add additional pages if content is longer than one page
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Generate filename from myday description and date
    const date = new Date(myday.date);
    const dateStr = date.toISOString().split('T')[0];
    const description = myday.description || 'myday';
    const safeDescription = description.replace(/[^a-z0-9]/gi, '_').substring(0, 50);
    const fileName = `${safeDescription}-${dateStr}.pdf`;

    // Download the PDF
    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};
